// Click-to-sort column headers for the Time Off Console tables. Looks like the
// Access / Automation Logs dashboards (MUI TableSortLabel); clicks behave like
// the shared TanStackTable: text starts A→Z, numbers and dates start
// highest/newest first, and a third click returns to the default order.

// REACT IMPORTS
import React, { useMemo, useState } from "react";

// MUI COMPONENTS
import { TableCell, TableSortLabel } from "@mui/material";

const isBlank = (v) => v === null || v === undefined || v === "";

// Blanks always sort last; numbers and booleans numerically; text naturally
// ("Item 2" before "Item 10")
const compareValues = (a, b) => {
	if (typeof a === "boolean") a = Number(a);
	if (typeof b === "boolean") b = Number(b);
	if (typeof a === "number" && typeof b === "number") return a - b;
	return String(a).localeCompare(String(b), undefined, {
		numeric: true,
		sensitivity: "base",
	});
};

/**
 * Sorts rows by the active column.
 * @param {Array} rows
 * @param {Object} getters - { columnKey: (row) => value }; keep it stable
 *   (module constant or useMemo) so rows aren't re-sorted every render
 * @param {string} defaultKey
 * @param {"asc"|"desc"} defaultDir
 * @param {string[]} descFirst - columns whose first click sorts descending
 *   (numbers, dates, yes/no)
 * @returns {{ sorted: Array, sortKey: string, sortDir: string, onSort: Function }}
 */
export const useSortedRows = (
	rows,
	getters,
	defaultKey,
	defaultDir = "asc",
	descFirst = [],
) => {
	const [sortKey, setSortKey] = useState(defaultKey);
	const [sortDir, setSortDir] = useState(defaultDir);

	const sorted = useMemo(() => {
		const get = getters[sortKey];
		if (!get) return rows;
		const sign = sortDir === "asc" ? 1 : -1;
		return [...rows].sort((x, y) => {
			const a = get(x);
			const b = get(y);
			if (isBlank(a) || isBlank(b)) {
				return isBlank(a) === isBlank(b) ? 0 : isBlank(a) ? 1 : -1;
			}
			return compareValues(a, b) * sign;
		});
	}, [rows, getters, sortKey, sortDir]);

	const firstDir = (key) => (descFirst.includes(key) ? "desc" : "asc");

	// New column -> its first direction; again -> flipped; again -> default order
	const onSort = (key) => {
		if (key !== sortKey) {
			setSortKey(key);
			setSortDir(firstDir(key));
		} else if (sortDir === firstDir(key)) {
			setSortDir(sortDir === "asc" ? "desc" : "asc");
		} else {
			setSortKey(defaultKey);
			setSortDir(defaultDir);
		}
	};

	return { sorted, sortKey, sortDir, onSort };
};

// Header cell with a sort arrow
export const SortableHeaderCell = ({
	sortKey,
	activeKey,
	dir,
	onSort,
	align,
	children,
}) => (
	<TableCell
		align={align}
		sortDirection={activeKey === sortKey ? dir : false}
	>
		<TableSortLabel
			active={activeKey === sortKey}
			direction={activeKey === sortKey ? dir : "asc"}
			onClick={() => onSort(sortKey)}
		>
			{children}
		</TableSortLabel>
	</TableCell>
);
